// ML Insights Types

export enum InsightType {
  AIR_FILTER_CLOGGED = 'air_filter_clogged',
  AIR_FILTER_CLOGGED_DISPLAY = 'Clogged Air Filter',
  TPMS_SLOW_LEAK = 'tpms_slow_leak',
  TPMS_FAST_LEAK = 'tpms_fast_leak',
  TRANSMISSION_INEFFICIENCY = 'transmission_inefficiency',
}

export const INSIGHT_TYPE_KEYS = {
  AIR_FILTER: 'air_filter',
  TPMS: 'tpms',
  TRANSMISSION: 'transmission',
} as const;

/**
 * Insight types hidden from the Insights listing and its summary counts.
 * Compared lowercase against both `issue` and `insight`, since rows may carry
 * either. Remove an entry here to surface that insight again.
 */
export const HIDDEN_INSIGHT_TYPES = ['high turbo boost pressure'];

/**
 * True when an insight row should be hidden from the listing and its counts.
 * Shared by InsightsTable and Cards so the table and tiles stay consistent.
 */
export const isHiddenInsightType = (row: {
  issue?: string;
  insight?: string;
}): boolean => {
  const issue = row.issue?.trim().toLowerCase() ?? '';
  const insight = row.insight?.trim().toLowerCase() ?? '';
  return HIDDEN_INSIGHT_TYPES.some(
    (hidden) => issue === hidden || insight === hidden
  );
};

/** Detection mechanism labels returned by ml-insights-v2. */
export type DetectionType = 'AI Predicted' | 'Threshold';

export const DETECTION_TYPE_OPTIONS: DetectionType[] = [
  'AI Predicted',
  'Threshold',
];

export interface MLInsight {
  customer: string;
  source?: string;
  datetime?: string;
  device: string;
  diff?: string;
  health_category: string;
  health_score: number;
  insight?: string;
  issue?: string;
  task: string;
  status?: string | null;
  timestamp?: string;
  alertStartTs?: string;
  alertEndTs?: string;
  alterCount?: number;
  notificationId?: string;
  ruleId?: string;
  resolution?: string;
  severity?: string;
  type?: string;
  /** How the insight was detected: ML pipeline output vs user-configured rule */
  detectionType?: DetectionType | null;
  /** Which ML pipeline produced the row; null for rule-based notifications */
  insightSource?: 'anomaly_detection' | 'air_filter_rul' | null;
  /** Sensor / J1939 tag identifiers returned by ml-insights-v2 */
  tagNames?: string[];
  /** Rule conditions attached directly from ML-insights notification payload */
  ruleConditions?: InsightRuleCondition[];
}

export interface InsightRuleCondition {
  conditionId?: string;
  tagName: string;
  relationalOperator: RelationalOperator;
  value: number;
  unit?: string | null;
}

export type RelationalOperator = '>' | '<' | '>=' | '<=' | '=' | '!=';

export interface AirFilterInsight {
  date: string;
  rul_hours: number;
  filter: string;
  filter_health: string;
  date_partition: string;
  device: string;
  cust_code: string;
}

export interface MLInsightsSummary {
  num_of_insights: number;
  num_of_devices: number;
  num_of_issues: number;
}

export interface MLInsightsResponse {
  insights: MLInsight[];
  summary: MLInsightsSummary;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface MLImpactResponse {
  impact:
    | string
    | Array<{
        insight: string;
        days: number;
        avg_engine_hrs_anomaly: number;
        avg_engine_hrs_baseline: number;
        avg_engine_hrs_diff: number;
        avg_engine_hrs_diff_pct: number;
        exp_fuel_rate_diff: number;
      }>;
  details?: Record<string, unknown>;
}

export interface EvidenceDataPoint {
  ds?: string;
  datetime?: string;
  y_raw?: number | string;
  diff?: number;
  sample_timestamp?: number | string;
  value?: number | string;
  output_ratio?: number | string;
  name?: string;
  asset?: string;
  device?: string;
  customer?: string;
  /** Model-predicted bound the reading is compared against; null when absent */
  yhat_bound?: number | string | null;
  /** Which side of the forecast interval `yhat_bound` represents */
  bound_type?: string | null;
}

export interface SupportingEvidenceResponse {
  evidence: Record<string, unknown>;
  values?: Record<string, unknown>;
  evidenceValues?: EvidenceDataPoint[];
  queryWindow?: {
    fromTs: string;
    toTs: string;
  };
}

export interface UpsertInsightPayload {
  insightName: string;
  insightDate: string;
  deviceId: string;
  status: string;
}

export interface UpdatedInsight {
  insightName: string;
  deviceId: string;
  status: string;
  insightDate: string;
  insight?: string;
  device?: string;
  timestamp?: string;
  datetime?: string;
}

export interface UpdatedInsightsResponse {
  insights: UpdatedInsight[];
}

export interface AirFilterInsightsResponse {
  formattedInsights: MLInsight[];
  response: AirFilterInsight[];
}

export interface AirFilterEvidenceResponse {
  evidence: Record<string, unknown>;
  evidenceValues?: Record<string, unknown>;
}

export interface TpmsEvidenceResponse {
  evidence: Record<string, unknown>;
  evidenceValues?: Record<string, unknown>;
}

export interface TpmsDataPoint {
  sample_timestamp: string;
  pressure: number;
  forecast: number;
}

export interface ProcessedChartDataPoint {
  ts: number;
  originalTimestamp: string;
  value: number;
  formattedDate: string;
  /**
   * Model-predicted bound for this timestamp, in the same display units as
   * `value`. Undefined when the API omits it or sends null.
   */
  bound?: number;
  boundType?: string;
  /**
   * Lower edge of the reading-to-bound gap, used as an invisible stacked
   * baseline so the band above it starts at the right height.
   */
  gapBase?: number;
  /**
   * Height of the reading-to-bound gap. Stacked on `gapBase` to shade the
   * margin between the two series. Undefined when there is no bound.
   */
  gapSpan?: number;
}

export interface RechartsPayload {
  value?: number;
  payload?: ProcessedChartDataPoint;
  cx?: number | null;
  cy?: number | null;
  /** Series identifier — distinguishes the reading from the bound series */
  dataKey?: string;
}

export interface AirFilterDataPoint {
  sample_timestamp: string;
  j1939engineairfilter1differentialpressure?: number;
  j1939engineairfilter2differentialpressure?: number;
  j1939engineairfilter3differentialpressure?: number;
  j1939engineairfilter4differentialpressure?: number;
}

export interface AirFilterTooltipPayload {
  name: string;
  value?: number;
  color: string;
  payload?: AirFilterDataPoint;
}

export interface FilterVisibilityState {
  filter1: boolean;
  filter2: boolean;
  filter3: boolean;
  filter4: boolean;
}

export interface TableInsightItem extends MLInsight {
  issue?: string;
  severity?: string;
  type?: string;
  status?: string;
  resolution?: string;
}

export interface InsightsTableNavigationFilters {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  searchTerm: string;
  selectedView: string;
  selectedType: string;
  selectedIssues: string[];
  resetSignal: number;
}

export interface InsightsTableNavigationState {
  page: number;
  rowsPerPage: number;
  paginatedData: TableInsightItem[];
  selectedInsightId: string;
  filters: InsightsTableNavigationFilters;
}

/** Per-column filters applied on top of the tab/search/sort pipeline. */
export interface InsightsColumnFilters {
  insightName: string;
  assetId: string;
  system: string;
  severity: string;
  detectionType: string;
}

export interface InsightsTableProps {
  enableDarkTheme: boolean;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  sortOrder: 'asc' | 'desc';
  setSortOrder: (value: 'asc' | 'desc') => void;
  selectedView: string;
  setSelectedView: (value: string) => void;
  selectedType: string;
  setSelectedType: (value: string) => void;
  selectedIssues: string[];
  setSelectedIssues: (value: string[]) => void;
  restoredState?: InsightsTableNavigationState;
  resetSignal: number;
}
